// File: /src/components/ClientModalFunctions.js

import { useCallback, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { generateReceiptPdf } from '../components/GenReceipt';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import { AuthError } from '../utils/auth';
import apiClient from '../services/apiClient';
import { API_ENDPOINTS } from '../constants/apiEndpoints';

const isAuthError = (error) => error instanceof AuthError;

export function useClientModalFunctions(
	existingClients = [],
	refreshClients = null,
	refreshProjects = null,
	refreshInvoices = null,
	cachedProjects = null,
	cachedInvoices = null
) {
	// Estados do modal principal e suas seções
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedClient, setSelectedClient] = useState(null);
	const [projects, setProjects] = useState([]);
	const [invoices, setInvoices] = useState([]);
	// Novo estado para documentos
	const [documents, setDocuments] = useState([]);
	const [tabIndex, setTabIndex] = useState(0);

	// Estados para modais de edição
	const [clientEditModalOpen, setClientEditModalOpen] = useState(false);
	const [projectEditModalOpen, setProjectEditModalOpen] = useState(false);
	const [invoiceEditModalOpen, setInvoiceEditModalOpen] = useState(false);
	const [documentModalOpen, setDocumentModalOpen] = useState(false);

	// Estados locais dos formulários de edição
	const [localClientForm, setLocalClientForm] = useState({
		name: '',
		email: '',
		cpf_cnpj: '',
		phone: '',
		address: '',
	});
	const [localProjectForm, setLocalProjectForm] = useState({
		id: null,
		clientId: '',
		name: '',
		description: '',
		status: 'pending',
		value: '',
		due_date: null,
	});
	const [localInvoiceForm, setLocalInvoiceForm] = useState({
		id: null,
		projectId: '',
		description: '',
		value: '',
		due_date: null,
		status: 'pending',
	});

	// Flags para diferenciar edição de adição
	const [isEditingProject, setIsEditingProject] = useState(false);
	const [isEditingInvoice, setIsEditingInvoice] = useState(false);

	// Função que atualiza os dados do modal (projetos, faturas e documentos) para o cliente selecionado
	const refreshModalData = useCallback(async () => {
		if (!selectedClient) return;

		try {
			// Otimização: usar dados em cache se disponíveis, evitando refetch desnecessário
			const shouldFetchProjects = !cachedProjects || cachedProjects.length === 0;
			const shouldFetchInvoices = !cachedInvoices || cachedInvoices.length === 0;

			// Buscar apenas dados que não estão em cache
			const promises = [];
			let projectsData, invoicesData;

			if (shouldFetchProjects) {
				promises.push(
					apiClient.get(API_ENDPOINTS.PROJECTS).then(res => {
						projectsData = res.data;
					})
				);
			} else {
				projectsData = cachedProjects;
			}

			if (shouldFetchInvoices) {
				promises.push(
					apiClient.get(API_ENDPOINTS.INVOICES).then(res => {
						invoicesData = res.data;
					})
				);
			} else {
				invoicesData = cachedInvoices;
			}

			// Sempre buscar documentos (não são carregados globalmente)
			promises.push(
				apiClient.get(API_ENDPOINTS.DRIVE_ROOT).then(res => {
					return res.data;
				})
			);

			// Aguardar apenas as requisições necessárias
			const results = await Promise.all(promises);
			const documentsData = results[results.length - 1]; // Documentos sempre vem por último

			// Filtrar projetos do cliente
			const clientProjects = (projectsData || []).filter(
				(proj) => Number(proj.clientId) === Number(selectedClient.id)
			);
			setProjects(clientProjects);

			// Filtrar faturas relacionadas aos projetos do cliente
			const projectIds = new Set(
				clientProjects.map((project) => Number(project.id))
			);

			const clientInvoices = (invoicesData || []).filter((inv) =>
				projectIds.has(Number(inv.projectId))
			);
			setInvoices(clientInvoices);

			// Filtrar documentos do cliente
			const clientDocs = Array.isArray(documentsData)
				? documentsData.filter(
						(doc) => Number(doc.clientId) === Number(selectedClient.id)
				  )
				: [];
			setDocuments(clientDocs);
		} catch (err) {
			if (err instanceof AuthError) {
				return;
			}
			console.error('Erro ao carregar dados do cliente:', err);
			toast.error('Erro ao carregar dados do cliente.');
		}
	}, [selectedClient, cachedProjects, cachedInvoices]);

	// useEffect para atualizar os dados do modal sempre que selectedClient mudar
	useEffect(() => {
		if (selectedClient) {
			refreshModalData();
		}
	}, [selectedClient, refreshModalData]);

	// Abre o modal principal e carrega os dados do cliente
	const openClientModal = (client) => {
		setSelectedClient(client);
		setModalOpen(true);
		setTabIndex(0);
		setClientEditModalOpen(false);
		refreshModalData();
	};

	const closeClientModal = () => {
		setModalOpen(false);
		setSelectedClient(null);
		setProjects([]);
		setInvoices([]);
		setDocuments([]);
		setTabIndex(0);
	};

	const handleTabChange = (event, newIndex) => {
		setTabIndex(newIndex);
	};

	// Funções de edição do cliente
	const openClientEditModal = (clientData) => {
		setLocalClientForm({
			name: clientData.name,
			email: clientData.email,
			cpf_cnpj: clientData.cpf_cnpj,
			phone: clientData.phone,
			address: clientData.address,
		});
		setClientEditModalOpen(true);
	};

	const openNewClientModal = () => {
		setSelectedClient(null);
		setLocalClientForm({
			name: '',
			email: '',
			cpf_cnpj: '',
			phone: '',
			address: '',
		});
		setModalOpen(true);
		setClientEditModalOpen(true);
	};

	const handleClientInputChange = (e) => {
		setLocalClientForm({
			...localClientForm,
			[e.target.name]: e.target.value,
		});
	};

	const handleClientSubmit = async (e) => {
		e.preventDefault();
		// Validação de CPF/CNPJ e Email
		if (
			!cpf.isValid(localClientForm.cpf_cnpj) &&
			!cnpj.isValid(localClientForm.cpf_cnpj)
		) {
			toast.error('CPF/CNPJ inválido!');
			return;
		}
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(localClientForm.email)) {
			toast.error('Email inválido!');
			return;
		}
		if (
			!selectedClient &&
			existingClients.some((c) => c.email === localClientForm.email)
		) {
			toast.error('O email informado já está cadastrado.');
			return;
		}
		try {
			if (selectedClient && selectedClient.id) {
				await apiClient.put(
					API_ENDPOINTS.clientById(selectedClient.id),
					localClientForm,
					
				);
				toast.success('Cliente atualizado com sucesso!');
				setSelectedClient({ ...selectedClient, ...localClientForm });
				if (typeof refreshClients === 'function') refreshClients();
				refreshModalData();
				setClientEditModalOpen(false);
			} else {
				await apiClient.post(
					API_ENDPOINTS.CLIENTS,
					localClientForm,
					
				);
				toast.success('Cliente adicionado com sucesso!');
				if (typeof refreshClients === 'function') refreshClients();
				closeClientModal();
			}
		} catch (error) {
			if (isAuthError(error)) {
				return;
			}
			toast.error(
				error.response?.data?.details || 'Erro ao salvar cliente.'
			);
		}
	};

	// Funções de projetos (projetos)
	const openProjectEditModal = (projectData) => {
		setLocalProjectForm({
			id: projectData.id,
			clientId: String(projectData.clientId),
			name: projectData.name,
			description: projectData.description || '',
			status: projectData.status,
			value: projectData.value,
			start_date: projectData.start_date, // Adicionando start_date
			end_date: projectData.end_date, // Adicionando end_date
		});
		setIsEditingProject(true);
		setProjectEditModalOpen(true);
	};

	const openAddProjectModal = () => {
		setLocalProjectForm({
			id: null,
			clientId: String(selectedClient.id),
			name: '',
			description: '',
			status: 'pending',
			value: '',
			due_date: null,
		});
		setIsEditingProject(false);
		setProjectEditModalOpen(true);
	};

	const handleProjectInputChange = (e) => {
		setLocalProjectForm({
			...localProjectForm,
			[e.target.name]: e.target.value,
		});
	};

	const handleProjectSubmit = async (e) => {
		e.preventDefault();
		const payload = {
			...localProjectForm,
			value: parseFloat(localProjectForm.value),
			start_date: localProjectForm.start_date
				? new Date(localProjectForm.start_date).toISOString()
				: null,
			end_date: localProjectForm.end_date
				? new Date(localProjectForm.end_date).toISOString()
				: null,
		};

		try {
			if (isEditingProject && localProjectForm.id) {
				await apiClient.put(
					API_ENDPOINTS.projectById(localProjectForm.id),
					payload,
					
				);
				toast.success('Projeto atualizado com sucesso!');
			} else {
				await apiClient.post(
					API_ENDPOINTS.PROJECTS,
					payload,
					
				);
				toast.success('Projeto adicionado com sucesso!');
			}
			if (typeof refreshProjects === 'function') {
				await refreshProjects();
			}
			if (typeof refreshInvoices === 'function') {
				await refreshInvoices();
			}
			refreshModalData();
			setProjectEditModalOpen(false);
		} catch (error) {
			if (isAuthError(error)) {
				return;
			}
			console.error('Erro ao salvar projeto:', error);
			toast.error('Erro ao salvar projeto.');
		}
	};

	// Funções de faturas
	const openInvoiceEditModal = (invoiceData) => {
		setLocalInvoiceForm({
			id: invoiceData.id,
			projectId: invoiceData.projectId,
			description: invoiceData.description,
			value: invoiceData.value,
			due_date: invoiceData.due_date
				? new Date(invoiceData.due_date)
				: null,
			status: invoiceData.status,
		});
		setIsEditingInvoice(true);
		setInvoiceEditModalOpen(true);
	};

	const openAddInvoiceModal = (projectId = '') => {
		setLocalInvoiceForm({
			id: null,
			projectId: projectId || '',
			description: '',
			value: '',
			due_date: null,
			status: 'pending',
		});
		setIsEditingInvoice(false);
		setInvoiceEditModalOpen(true);
	};

	const handleInvoiceInputChange = (e) => {
		setLocalInvoiceForm({
			...localInvoiceForm,
			[e.target.name]: e.target.value,
		});
	};

	const handleInvoiceSubmit = async (e) => {
		e.preventDefault();
		const payload = {
			...localInvoiceForm,
			value: parseFloat(localInvoiceForm.value),
			due_date: localInvoiceForm.due_date
				? localInvoiceForm.due_date.toISOString()
				: null,
		};
		try {
			if (isEditingInvoice && localInvoiceForm.id) {
				await apiClient.put(
					API_ENDPOINTS.invoiceById(localInvoiceForm.id),
					payload,
					
				);
				toast.success('Fatura atualizada com sucesso!');
			} else {
				await apiClient.post(
					API_ENDPOINTS.INVOICES,
					payload,
					
				);
				toast.success('Fatura criada com sucesso!');
			}
			if (typeof refreshProjects === 'function') {
				await refreshProjects();
			}
			if (typeof refreshInvoices === 'function') {
				await refreshInvoices();
			}
			refreshModalData();
			setInvoiceEditModalOpen(false);
		} catch (error) {
			if (isAuthError(error)) {
				return;
			}
			console.error('Erro ao salvar fatura:', error);
			toast.error('Erro ao salvar fatura.');
		}
	};

	// Funções de exclusão
	const handleDeleteClient = async () => {
	try {
		await apiClient.delete(
			API_ENDPOINTS.clientById(selectedClient.id),
			
		);
			toast.success('Cliente excluído com sucesso!');
			closeClientModal();
			if (typeof refreshClients === 'function') refreshClients();
		} catch (error) {
			if (isAuthError(error)) {
				return;
			}
			console.error('Erro ao excluir cliente:', error);
			toast.error('Erro ao excluir cliente.');
		}
	};

	const handleDeleteProject = async (projectId) => {
	try {
		await apiClient.delete(
			API_ENDPOINTS.projectById(projectId),
			
		);
			toast.success('Projeto excluído com sucesso!');
			if (typeof refreshProjects === 'function') {
				await refreshProjects();
			}
			refreshModalData();
		} catch (error) {
			if (isAuthError(error)) {
				return;
			}
			console.error('Erro ao excluir projeto:', error);
			toast.error('Erro ao excluir projeto.');
		}
	};

	const handleDeleteInvoice = async (invoiceId) => {
	try {
		await apiClient.delete(
			API_ENDPOINTS.invoiceById(invoiceId),
			
		);
			toast.success('Fatura excluída com sucesso!');
			if (typeof refreshInvoices === 'function') {
				await refreshInvoices();
			}
			refreshModalData();
		} catch (error) {
			if (isAuthError(error)) {
				return;
			}
			console.error('Erro ao excluir fatura:', error);
			toast.error('Erro ao excluir fatura.');
		}
	};

	// NOVAS FUNÇÕES PARA DOCUMENTOS

	// Abre o modal de upload de documento
	const openAddDocumentModal = () => {
		setDocumentModalOpen(true);
	};

	// Faz o download do documento dado seu ID
	const handleDocumentDownload = async (documentId) => {
	try {
		const response = await apiClient.get(
			`${API_ENDPOINTS.DRIVE_ROOT}/${documentId}/download`,
			{
				responseType: 'blob',
			}
		);
			// Cria uma URL para o blob e dispara o download
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			// Procura o documento na lista para usar seu nome; caso não encontre, usa "documento"
			const doc = documents.find(
				(d) => Number(d.id) === Number(documentId)
			);
			const fileName = doc ? doc.name : 'documento';
			link.href = url;
			link.setAttribute('download', fileName);
			document.body.appendChild(link);
			link.click();
			link.parentNode.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			if (isAuthError(error)) {
				return;
			}
			console.error('Erro ao baixar documento:', error);
			toast.error('Erro ao baixar documento.');
		}
	};

	// Exclui o documento dado seu ID e atualiza a lista
	const handleDeleteDocument = async (documentId) => {
	try {
		await apiClient.delete(
			`${API_ENDPOINTS.DRIVE_ROOT}/${documentId}`,
			
		);
			toast.success('Documento excluído com sucesso!');
			// Atualiza a lista de documentos
			refreshModalData();
		} catch (error) {
			if (isAuthError(error)) {
				return;
			}
			console.error('Erro ao excluir documento:', error);
			toast.error('Erro ao excluir documento.');
		}
	};

	// Função para emitir recibo
	const generateReceipt = (invoiceIds) => {
		generateReceiptPdf(invoices, invoiceIds, [selectedClient], projects);
	};

	const handleArchiveClient = async (clientId, shouldArchive = true) => {
		try {
		const client =
			selectedClient ||
			(await apiClient.get(
				API_ENDPOINTS.clientById(clientId),
			)).data;
			if (!client) {
				throw new Error('Cliente não encontrado');
			}
			const newArchivedStatus =
				shouldArchive !== undefined ? shouldArchive : !client.archived;
		await apiClient.put(
			API_ENDPOINTS.clientById(clientId),
			{ ...client, archived: newArchivedStatus },
		);
			if (selectedClient) {
				setSelectedClient({ ...client, archived: newArchivedStatus });
			}
			if (typeof refreshClients === 'function') refreshClients();
		} catch (error) {
			if (isAuthError(error)) {
				return;
			}
			console.error('Erro ao arquivar/desarquivar cliente:', error);
			throw error;
		}
	};

	const handleArchiveProject = async (projectId, shouldArchive = true) => {
		try {
		const projectResponse = await apiClient.get(
			API_ENDPOINTS.projectById(projectId),
		);
			const project = projectResponse.data;
			if (!project) {
				throw new Error('Projeto não encontrado');
			}
			const newArchivedStatus =
				shouldArchive !== undefined ? shouldArchive : !project.archived;
		await apiClient.put(
			API_ENDPOINTS.projectById(projectId),
			{ ...project, archived: newArchivedStatus },
		);
			refreshModalData();
			if (typeof refreshProjects === 'function') refreshProjects();
		} catch (error) {
			if (isAuthError(error)) {
				return;
			}
			console.error('Erro ao arquivar/desarquivar projeto:', error);
			throw error;
		}
	};

	const handleArchiveInvoice = async (invoiceId, shouldArchive = true) => {
		try {
		const invoiceResponse = await apiClient.get(
			API_ENDPOINTS.invoiceById(invoiceId),
		);
			const invoice = invoiceResponse.data;
			if (!invoice) {
				throw new Error('Fatura não encontrada');
			}
			const newArchivedStatus =
				shouldArchive !== undefined ? shouldArchive : !invoice.archived;
		await apiClient.put(
			API_ENDPOINTS.invoiceById(invoiceId),
			{ ...invoice, archived: newArchivedStatus },
		);
			refreshModalData();
			if (typeof refreshInvoices === 'function') refreshInvoices();
		} catch (error) {
			if (isAuthError(error)) {
				return;
			}
			console.error('Erro ao arquivar/desarquivar fatura:', error);
			throw error;
		}
	};

	// Fechamento dos modais de edição
	const closeClientEditModal = () => setClientEditModalOpen(false);
	const closeProjectEditModal = () => setProjectEditModalOpen(false);
	const closeInvoiceEditModal = () => setInvoiceEditModalOpen(false);

	// Adicione estas novas funções dentro do hook useClientModalFunctions

	const handleDocumentUpload = async (file) => {
		try {
			const formData = new FormData();
			formData.append('file', file);
			formData.append('clientId', selectedClient.id);

	const response = await apiClient.post(
		API_ENDPOINTS.DRIVE_UPLOAD,
		formData,
		{
			headers: {
				'Content-Type': 'multipart/form-data',
			},
			validateStatus: function (status) {
				return status < 500;
			},
		}
	);			if (response.status === 400) {
				toast.error(response.data.error);
				return;
			}

			toast.success('Documento enviado com sucesso!');
			setDocumentModalOpen(false);

			if (response.data) {
				setDocuments((prev) => [...(prev || []), response.data]);
			}
			refreshModalData();
		} catch (error) {
			if (isAuthError(error)) {
				return;
			}
			const errorMessage =
				error.response?.data?.error || 'Erro ao enviar documento.';
			toast.error(errorMessage);
			console.error('Erro ao enviar documento:', error);
		}
	};

	const closeDocumentModal = () => setDocumentModalOpen(false);

	return {
		modalOpen,
		selectedClient,
		projects,
		invoices,
		documents,
		tabIndex,
		openClientModal,
		closeClientModal,
		handleTabChange,
		openClientEditModal,
		openNewClientModal,
		handleClientInputChange,
		handleClientSubmit,
		clientEditModalOpen,
		localClientForm,
		openProjectEditModal,
		openAddProjectModal,
		handleProjectInputChange,
		handleProjectSubmit,
		projectEditModalOpen,
		localProjectForm,
		isEditingProject,
		openInvoiceEditModal,
		openAddInvoiceModal,
		handleInvoiceInputChange,
		handleInvoiceSubmit,
		invoiceEditModalOpen,
		localInvoiceForm,
		isEditingInvoice,
		handleDeleteClient,
		handleDeleteProject,
		handleDeleteInvoice,
		handleDeleteDocument,
		handleDocumentDownload,
		openAddDocumentModal,
		closeClientEditModal,
		closeProjectEditModal,
		closeInvoiceEditModal,
		generateReceipt,
		handleArchiveClient,
		handleArchiveProject,
		handleArchiveInvoice,
		documentModalOpen,
		handleDocumentUpload,
		closeDocumentModal,
	};
}
